import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { API_BASE_URL as BASE_URL } from "../../../services/api";
import { DEFAULT_COMPANY_INFO } from "../constants/settingsTabs";
import { getCompanySettings, updateCompanySettings, uploadCompanyLogo } from "../services/settingsService";

const formatLogoUrl = (path) => {
  if (!path || path === "undefined" || path === "null") return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;

  // Fix Windows backslashes
  let cleanPath = path.replace(/\\/g, '/');

  // Ensure the path starts with a slash
  cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  return `${BASE_URL}${cleanPath}`;
};

export const useCompanyInfo = () => {
  const [company, setCompany] = useState(DEFAULT_COMPANY_INFO);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCompanySettings = async () => {
    try {
      const res = await getCompanySettings();
      const payload = res?.data?.data || res?.data || {};
      const mapped = {
        name: payload.companyName || DEFAULT_COMPANY_INFO.name,
        email: payload.companyEmail || DEFAULT_COMPANY_INFO.email,
        phone: payload.companyPhone || DEFAULT_COMPANY_INFO.phone,
        address: payload.companyAddress || DEFAULT_COMPANY_INFO.address,
        gst: payload.companyGst || DEFAULT_COMPANY_INFO.gst,
        pan: payload.companyPan || "",
        state: payload.companyState || "",
        stateCode: payload.companyStateCode || "",
        website: payload.companyWebsite || DEFAULT_COMPANY_INFO.website,
        logoUrl: formatLogoUrl(payload.logoExpandedPath),
        digitalSignatureUrl: formatLogoUrl(payload.digitalSignaturePath),
      };
      setCompany(mapped);
    } catch {
      // keep local defaults silently
    }
  };

  useEffect(() => {
    loadCompanySettings();

    const handleUpdate = () => loadCompanySettings();
    const handlePreview = (e) => {
      if (e.detail !== undefined) {
        setCompany((prev) => ({ ...prev, logoUrl: e.detail }));
      }
    };
    window.addEventListener("companySettingsUpdated", handleUpdate);
    window.addEventListener("companyLogoPreviewUpdated", handlePreview);
    return () => {
      window.removeEventListener("companySettingsUpdated", handleUpdate);
      window.removeEventListener("companyLogoPreviewUpdated", handlePreview);
    };
  }, []);

  const saveCompanyInfo = async () => {
    setIsSaving(true);
    try {
      // We don't need to upload logoFile or signatureFile here because
      // handleLogoFile and handleSignatureFile in CompanyInfoTab.jsx
      // already call uploadLogoOnly and uploadSignatureOnly immediately.
      
      // Strip BASE_URL from the URLs before saving so the DB only stores relative paths
      const cleanLogoUrl = company.logoUrl ? company.logoUrl.replace(BASE_URL, "") : "";
      const cleanSignatureUrl = company.digitalSignatureUrl ? company.digitalSignatureUrl.replace(BASE_URL, "") : "";

      await updateCompanySettings({
        companyName: company.name,
        companyEmail: company.email,
        companyPhone: company.phone,
        companyAddress: company.address,
        companyGst: company.gst,
        companyPan: company.pan,
        companyState: company.state,
        companyStateCode: company.stateCode,
        companyWebsite: company.website,
        logoExpandedPath: cleanLogoUrl,
        digitalSignaturePath: cleanSignatureUrl,
      });

      toast.success("Settings saved!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to save company settings");
      return false;
    } finally {
      setIsSaving(false);
      setLogoFile(null);
      setSignatureFile(null);
    }
  };

  const setCompanyLogo = (logoUrl) => {
    setCompany((prev) => ({ ...prev, logoUrl }));
    window.dispatchEvent(new CustomEvent("companyLogoPreviewUpdated", { detail: logoUrl }));
  };

  const uploadLogoOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append("logoExpanded", file);
      await uploadCompanyLogo(formData);
      toast.success("Logo uploaded successfully!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
    } catch (error) {
      toast.error("Failed to upload logo.");
    }
  };

  const uploadSignatureOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append("digitalSignature", file);
      await uploadCompanyLogo(formData);
      toast.success("Signature uploaded successfully!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
    } catch (error) {
      toast.error("Failed to upload signature.");
    }
  };

  return { company, setCompany, saveCompanyInfo, isSaving, setCompanyLogo, setLogoFile, uploadLogoOnly, setSignatureFile, uploadSignatureOnly };
};
